import { z } from "zod";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import type { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    RecurrenceLeadUnit,
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    WorkEndDateMode,
    WorkStartDateMode,
} from "@common/types/serviceTemplateRecurrence";
import {
    buildEntityRowsFromWorkspaces,
    refineSelectedEntityRowAssignees,
    scheduleDefaultsSchema,
    type ApplyCloseTemplateWizardValues,
} from "./applyCloseTemplate.schema";
import { getLastCompletedMonthDates } from "./closePeriodSelector.utils";

const scheduleStep1FormSchema = z
    .object({
        defaults: z.object({
            preparerId: z.number().int().nullable().optional(),
            reviewerId: z.number().int().nullable().optional(),
            managerId: z.number().int().nullable().optional(),
            schedule: scheduleDefaultsSchema,
        }),
    })
    .superRefine(({ defaults }, ctx) => {
        const schedule = defaults.schedule;
        if (schedule.period === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "period"],
                message: "Select a recurrence period",
            });
        }
        if (
            schedule.endDate !== null &&
            schedule.endDate < schedule.startDate
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "endDate"],
                message: "End date must be on or after start date",
            });
        }
        if (schedule.endDate !== null && schedule.maxOccurrenceCount !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "maxOccurrenceCount"],
                message:
                    "Set either an end date or a max occurrence count, not both",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.WEEKLY &&
            schedule.weekDays.length < 1
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "weekDays"],
                message: "Select at least one weekday for weekly schedules",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.MONTHLY &&
            schedule.datesOfMonth.length < 1
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "datesOfMonth"],
                message: "Add at least one day of month for monthly schedules",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.QUARTERLY &&
            schedule.quarterMonth === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "quarterMonth"],
                message: "Select which month of the quarter",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.QUARTERLY &&
            schedule.dateOfMonth === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "dateOfMonth"],
                message: "Select a day of month for quarterly schedules",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.ANNUALLY &&
            schedule.month === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "month"],
                message: "Select a month for annual schedules",
            });
        }
        if (
            schedule.period === ServiceTemplateRecurrencePeriod.ANNUALLY &&
            schedule.dateOfMonth === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "dateOfMonth"],
                message: "Select a day of month for annual schedules",
            });
        }
        if (
            schedule.workStartDateMode ===
                WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END &&
            (schedule.workStartWeekday === null ||
                schedule.workStartWeekday < 1 ||
                schedule.workStartWeekday > 5)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaults", "schedule", "workStartWeekday"],
                message:
                    "Select a weekday (Monday-Friday) for the next-weekday work start mode",
            });
        }
    });

/** Occurrences step: validates the recurrence/work defaults shared by every entity the schedule is created for. */
export function createScheduleStep1FormSchema() {
    return scheduleStep1FormSchema;
}

const scheduleStep2ValidationSchema = z
    .object({
        entityRows: z.record(
            z.string(),
            z.object({
                isSelected: z.boolean(),
                entityUuid: z.string().uuid().nullable().optional(),
                workspaceId: z.string().uuid(),
                preparerId: z.number().int().nullable().optional(),
                reviewerId: z.number().int().nullable().optional(),
                managerId: z.number().int().nullable().optional(),
            }),
        ),
    })
    .refine(
        (data) => Object.values(data.entityRows).some((row) => row.isSelected),
        {
            message: "Select at least one entity",
            path: ["entityRows"],
        },
    );

/** Entity selection: schedules don't take per-entity dates, but assignees can be set per entity. */
export function createScheduleStep2ValidationSchema(
    members: OrganizationMember[],
    activeRoles: Set<TaskTemplateAssigneeRole>,
) {
    return scheduleStep2ValidationSchema.superRefine((data, ctx) => {
        refineSelectedEntityRowAssignees({
            entityRows: data.entityRows,
            ctx,
            members,
            activeRoles,
        });
    });
}

export function createScheduleWizardInitialValues(
    workspaces: WorkspaceSchema[] | undefined,
): ApplyCloseTemplateWizardValues {
    const entityRows = buildEntityRowsFromWorkspaces(workspaces);
    const { startDate: lastCompletedMonthStart } = getLastCompletedMonthDates();
    return {
        defaults: {
            startDate: null,
            endDate: null,
            workStartDate: null,
            workDeliveryDate: null,
            preparerId: null,
            reviewerId: null,
            managerId: null,
            schedule: {
                period: ServiceTemplateRecurrencePeriod.MONTHLY,
                interval: 1,
                startDate: lastCompletedMonthStart,
                endDate: null,
                maxOccurrenceCount: null,
                applicationLeadValue: 1,
                applicationLeadUnit: RecurrenceLeadUnit.DAYS,
                weekDays: [],
                dateOfMonth: null,
                datesOfMonth: [1],
                month: null,
                quarterMonth: null,
                workStartDateMode:
                    WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END,
                workStartOffsetDays: 1,
                workStartSkipWeekend: true,
                workStartWeekday: null,
                workEndDateMode:
                    WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START,
                workEndOffsetDays: null,
                // Last weekday of the month containing work start ("work month").
                workEndRelativePeriod: RelativeCalendarPeriod.MONTH,
                workEndRelativePosition: RelativeCalendarPosition.SAME,
                workEndRelativeDayType: RelativeDayType.LAST,
                workEndRelativeDayOfPeriod: null,
            },
        },
        entityRows,
    };
}
